import { LitElement, html, css } from 'lit';
import { ref, createRef } from 'lit/directives/ref.js';
import { when } from 'lit/directives/when.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import '../katapult-elements/katapult-dialog.js';
import '../katapult-elements/katapult-button.js';
import '@shoelace-style/shoelace/dist/components/checkbox/checkbox.js';

/** @import { Ref } from 'lit/directives/ref.js' */
/** @import { KatapultDialog } from '../katapult-elements/katapult-dialog.js' */
/** @import { KatapultButton } from '../katapult-elements/katapult-button.js' */
/** @import Firebase from 'firebase/compat/app' */

class PrivateServerTerms extends LitElement {
  static styles = css`
    h2,
    p {
      margin: var(--sl-spacing-small) 0;
      color: var(--sl-color-neutral-900);
    }
  `;

  static properties = {
    signedIn: { type: Boolean },
    userId: { type: String },
    _ownerName: { type: String, state: true },
    _serverHasTerms: { type: Boolean, state: true },
    _serverTermsLink: { type: String, state: true },
    _userTermsRecord: { type: Object, state: true },
    _userHasReadAndAccepted: { type: Boolean, state: true }
  };

  constructor() {
    super();

    /** @type {boolean} */
    this.signedIn = false;
    /** @type {?string} */
    this.userId = null;
    /** @type {string} */
    this._ownerName = '';
    /** @type {boolean} */
    this._serverHasTerms = false;
    /** @type {?string} */
    this._serverTermsLink = null;
    /** @type {?Object} */
    this._userTermsRecord = null;
    /** @type {boolean} */
    this._userHasReadAndAccepted = false;
  }

  /** @type {Ref<KatapultDialog>} */
  #termsDialogRef = createRef();

  /** @type {?function} */
  #unsubscribeListeners = null;

  render() {
    return html`
      <katapult-dialog ${ref(this.#termsDialogRef)} modal title="Private Server Terms" maxWidth="600">
        <h2>Before we begin...</h2>
        <p>
          You are accessing a Katapult Pro private server${this._ownerName ? html` owned by <b>${this._ownerName}</b>` : ''}.
          ${when(
            this._serverTermsLink,
            () => html`
              Before continuing, please read and accept this server's
              <!-- @ts-ignore Even with ifDefined, plugin reports non-nullable error (https://github.com/runem/lit-analyzer/issues/253) -->
              <a href=${ifDefined(this._serverTermsLink)} target="_blank">Terms</a>.
            `,
            () => html`
              By continuing, you acknowledge that ${this._ownerName || 'the server owner'} has access to and control over all data on this
              server.
            `
          )}
        </p>
        <p>
          <sl-checkbox @sl-input=${(event) => (this._userHasReadAndAccepted = event.currentTarget.checked)}
            >I have read and accept these Terms</sl-checkbox
          >
        </p>
        <katapult-button
          slot="buttons"
          color="var(--secondary-color)"
          ?disabled=${!this._userHasReadAndAccepted}
          @click=${this.agreeAndContinue}
          >Continue</katapult-button
        >
      </katapult-dialog>
    `;
  }

  shouldUpdate(changedProperties) {
    if (changedProperties.has('signedIn') || changedProperties.has('userId')) {
      this.#unsubscribeListeners?.();

      if (this.signedIn && this.userId) {
        const realtimeDb = /** @type {Firebase.database.Database} */ (globalThis.FirebaseWorker.database());

        // Get the server owner name
        realtimeDb.ref('photoheight/white_label/server_owner').once('value', async (snapshot) => {
          const ownerId = snapshot.val();
          // If we have an owner ID, look up the company name
          if (ownerId) this._ownerName = (await realtimeDb.ref(`photoheight/companies/${ownerId}/name`).once('value')).val() ?? ownerId;
        });
        // Check if the server has an additional Terms (in addition to the standard Katapult Pro EULA)
        realtimeDb.ref('photoheight/white_label/private_server_terms').once('value', (snapshot) => {
          this._serverHasTerms = snapshot.val() !== null;
          this._serverTermsLink = typeof snapshot.val() === 'string' ? snapshot.val() : null;
        });

        const termsRecordRef = realtimeDb.ref(`legal_agreements/private_server_terms/${this.userId}`);
        const termsRecordCallback = termsRecordRef.on('value', (snapshot) => {
          // Set the record to the value, or a default if none exists (must be non-null to indicate loaded)
          this._userTermsRecord = snapshot.val() ?? { accepted: false };
        });

        // Set up unsubscribe function
        this.#unsubscribeListeners = () => {
          termsRecordRef.off('value', termsRecordCallback);
          this._userTermsRecord = null;
        };

        return false;
      }
    }

    return true;
  }

  willUpdate(changedProperties) {
    if (changedProperties.has('signedIn') || changedProperties.has('_serverHasTerms') || changedProperties.has('_userTermsRecord')) {
      const termsNotAccepted = this._userTermsRecord?.accepted === false;
      if (this.signedIn && this._serverHasTerms && termsNotAccepted) {
        this.#termsDialogRef.value?.open();
      } else {
        this.#termsDialogRef.value?.close();
      }
    }
  }

  agreeAndContinue(event) {
    if (!this.signedIn || !this.userId) return;

    const button = /** @type {KatapultButton} */ (event.currentTarget);

    const realtimeDb = /** @type {Firebase.database.Database} */ (globalThis.FirebaseWorker.database());
    const termsRecordRef = realtimeDb.ref(`legal_agreements/private_server_terms/${this.userId}`);

    termsRecordRef
      .set({
        accepted: true,
        timestamp: globalThis.FirebaseWorker.database.ServerValue.TIMESTAMP
      })
      .catch((error) => {
        console.error('Error saving Terms acceptance:', error);
        button.showError('There was an error saving your acceptance. Please refresh and try again.');
      });
  }
}

window.customElements.define('private-server-terms', PrivateServerTerms);
